import React, { createContext, useContext, useState, useEffect } from 'react';
import API_BASE from '../api/config';

const UserContext = createContext();

export function UserProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            setIsAuthLoading(false);
            return;
        }

        const cachedUserRaw = localStorage.getItem('currentUser');
        if (cachedUserRaw) {
            try {
                const cachedUser = JSON.parse(cachedUserRaw);
                if (cachedUser && cachedUser.id) {
                    setCurrentUser(cachedUser);
                    checkAdminStatus(cachedUser.id);
                }
            } catch {
                localStorage.removeItem('currentUser');
            }
        }

        // Validate stored token against API
        fetch(`${API_BASE}/auth/validate-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        })
            .then(async (res) => {
                if (res.ok) return res.json();
                if (res.status === 401) {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('currentUser');
                    setCurrentUser(null);
                    setIsAdmin(false);
                    return null;
                }
                throw new Error(`Token validation temporary failure (${res.status})`);
            })
            .then(data => {
                if (!data || !data.user) return;
                setCurrentUser(data.user);
                localStorage.setItem('currentUser', JSON.stringify(data.user));
                if (data.user && data.user.id) checkAdminStatus(data.user.id);
            })
            .catch((err) => {
                console.warn('Token validation failed temporarily, preserving local session', err);
            })
            .finally(() => setIsAuthLoading(false));
    }, []);

    const checkAdminStatus = async (userId) => {
        try {
            const res = await fetch(`${API_BASE}/users/${userId}/is-admin`);
            if (res.ok) {
                const data = await res.json();
                setIsAdmin(data.isAdmin);
            } else {
                setIsAdmin(false);
            }
        } catch {
            setIsAdmin(false);
        }
    };

    const login = (user, token) => {
        localStorage.setItem('authToken', token);
        localStorage.setItem('currentUser', JSON.stringify(user));
        setCurrentUser(user);
        if (user && user.id) checkAdminStatus(user.id);
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
        setCurrentUser(null);
        setIsAdmin(false);
    };

    return (
        <UserContext.Provider value={{ currentUser, isAdmin, isAuthLoading, login, logout }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}

